import { Button } from "@gigpro/ui/Button";
import { DialogFooter } from "@gigpro/ui/Dialog";
import { Form } from "@gigpro/ui/Form";
import { DateField } from "@gigpro/ui/Form/DateField";
import { MaskedTextfield, wageMaskProps } from "@gigpro/ui/Form/MaskedTextfield";
import { Select, SelectItem } from "@gigpro/ui/Form/Select";
import { useNow } from "@gigpro/ui/utils/useNow";
import React from "react";
import type { SubmitHandler, UseFormReturn } from "react-hook-form";
import * as z from "zod/mini";

const MAX_WAGE = 99.99;

export const directHireSchema = z.object({
  position: z.number({ message: "Select a position" }),
  job_type: z.number({ message: "Select a job type" }),
  wage: z.coerce
    .number()
    .check(z.gt(0, "Enter a wage above $0/hr"), z.lte(MAX_WAGE, `Wage cannot exceed $${MAX_WAGE}/hr`)),
  expires_at: z.date({ message: "Choose an acceptance date" }),
});
export type DirectHireValues = z.infer<typeof directHireSchema>;

interface DirectHireFormProps {
  form: UseFormReturn<DirectHireValues>;
  proName: string;
  positions?: Array<{ id: number; name: string }>;
  jobTypes?: Array<{ id: number; name: string }>;
  /** The Pro's booked shifts a full-time hire would cancel; absent while the preview loads. */
  bookedShiftCount?: number;
  fullTimeSelected: boolean;
  onSubmit: SubmitHandler<DirectHireValues>;
  onCancel: () => void;
  /** Escape hatch back to the job picker, shown when the business would rather post a job. */
  onPostJobInstead?: () => void;
}

const fullTimeNote = (bookedShiftCount?: number) => {
  const lead = "They'll stop picking up shifts here, and";
  if (bookedShiftCount == null) return `${lead} any booked shifts will be cancelled.`;
  const noun = bookedShiftCount === 1 ? "shift" : "shifts";
  return `${lead} their ${bookedShiftCount} booked ${noun} will be cancelled.`;
};

export const DirectHireForm: React.FC<DirectHireFormProps> = ({
  form,
  proName,
  positions,
  jobTypes,
  bookedShiftCount,
  fullTimeSelected,
  onSubmit,
  onCancel,
  onPostJobInstead,
}) => {
  const now = useNow({ updateIntervalMs: false });
  const { control } = form;

  return (
    <Form form={form} onSubmit={onSubmit} className="flex flex-col gap-4">
      <p className="text-normal text-black">
        Hire {proName} without posting a job. Only you and {proName} will see it.
      </p>
      <Select
        control={control}
        name="position"
        label="Position"
        placeholder={positions ? "Line Cook" : "Loading..."}
        disabled={!positions}
        transform={{ input: String, output: Number }}
      >
        {positions?.map((position) => (
          <SelectItem key={position.id} value={String(position.id)}>
            {position.name}
          </SelectItem>
        ))}
      </Select>
      <div className="flex flex-col gap-1">
        <Select
          control={control}
          name="job_type"
          label="Job Type"
          placeholder={jobTypes ? "Full-time" : "Loading..."}
          disabled={!jobTypes}
          transform={{ input: String, output: Number }}
        >
          {jobTypes?.map((jobType) => (
            <SelectItem key={jobType.id} value={String(jobType.id)}>
              {jobType.name}
            </SelectItem>
          ))}
        </Select>
        {fullTimeSelected && <p className="text-small text-medium-gray">{fullTimeNote(bookedShiftCount)}</p>}
      </div>
      <MaskedTextfield control={control} name="wage" label="Hourly Wage" placeholder="$24.00/hr" {...wageMaskProps} />
      <DateField
        control={control}
        name="expires_at"
        label="Acceptance Date"
        description="We recommend a 3 day (72 hour) window"
        minDate={now}
        compact
      />
      <DialogFooter className="gap-4">
        <Button type="button" variant="text" className="flex-1" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" className="flex-1">
          Next
        </Button>
      </DialogFooter>
      {onPostJobInstead && (
        <Button type="button" variant="text" onClick={onPostJobInstead}>
          Post a job instead
        </Button>
      )}
    </Form>
  );
};
